import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { setWebTitle } from '@/lib/format-utils';
import { useTicket, useUpdateTicket, useCreateTicket, useDeleteTicket, useUsers } from '@/hooks/api-hooks';
import PageHeader from '@/components/page-header';
import { LoaderMainLogo } from '@/components/logo-loader';
import { useState, useEffect } from 'react';
import { Button, Select, Badge } from '@mantine/core';
import { Ticket, TICKET_STATUSES, TICKET_PRIORITIES } from '@turizi/shared';
import { TICKETS_PATH } from '@/lib/navigation';
import TicketEditor from '@/components/ticket';
import TicketComments from '@/components/ticket-comments';
import { notify } from '@/lib/notify';
import { IconArrowLeft, IconTrash, IconDeviceFloppy, IconLoader2 } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import i18next from 'i18next';
import DatePickerCustom from '@/components/custom-date-picker';

dayjs.extend(relativeTime);

const PRIORITY_COLORS: Record<string, { dot: string; text: string; bg: string }> = {
    low: { dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' },
    medium: { dot: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
    high: { dot: 'bg-amber-600', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
    urgent: { dot: 'bg-rose-600', text: 'text-rose-700 font-bold', bg: 'bg-rose-50 border-rose-200' },
};

export default function EditTicketPage(): JSX.Element {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const { data, isLoading } = useTicket(id, { enabled: !isNew });
    const { data: usersResponse } = useUsers();
    const updateMutation = useUpdateTicket();
    const createMutation = useCreateTicket();
    const deleteMutation = useDeleteTicket();

    const ticket = data?.data;
    const users = usersResponse?.data || [];

    const [formData, setFormData] = useState<Partial<Ticket>>({
        title: '',
        content: '',
        status: 'pending',
        priority: 'low',
        assignee_id: null,
        due_datetime: null,
    });

    useEffect(() => {
        if (ticket && !isNew) {
            setFormData({
                id: ticket.id,
                title: ticket.title,
                content: ticket.content,
                status: ticket.status,
                priority: ticket.priority,
                assignee_id: ticket.assignee_id,
                due_datetime: ticket.due_datetime ? new Date(ticket.due_datetime) : null,
            });
        }
    }, [ticket, isNew]);

    setWebTitle(isNew ? t('new_ticket') : ticket?.title || t('ticket'));

    const handleMetaChange = async (field: keyof Ticket, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (!isNew && ticket?.id) {
            try {
                await updateMutation.mutateAsync({
                    id: ticket.id,
                    [field]: value
                });
                notify.success('success', 'ticket_updated_success');
            } catch (error) {
                console.error(`Error updating ticket ${field}:`, error);
                notify.error('error', 'error');
            }
        }
    };

    const handleSaveTitleContent = async () => {
        try {
            if (isNew) {
                const response = await createMutation.mutateAsync(formData);
                notify.success('success', 'ticket_saved_success');
                const createdId = response?.data?.id;
                if (createdId) {
                    navigate(`${TICKETS_PATH}/${createdId}`, { replace: true });
                } else {
                    navigate(TICKETS_PATH);
                }
            } else if (ticket?.id) {
                await updateMutation.mutateAsync({
                    id: ticket.id,
                    title: formData.title,
                    content: formData.content,
                });
                notify.success('success', 'ticket_saved_success');
            }
        } catch (error) {
            console.error('Error saving ticket:', error);
            notify.error('error', 'error');
        }
    };

    const handleDelete = async () => {
        if (!ticket?.id) return;
        if (confirm(t('confirm_delete'))) {
            try {
                await deleteMutation.mutateAsync(ticket.id);
                notify.success('success', 'success');
                navigate(TICKETS_PATH);
            } catch (error) {
                console.error('Error deleting ticket:', error);
                notify.error('error', 'error');
            }
        }
    };

    if (isLoading && !isNew) {
        return (
            <div>
                <PageHeader title={t('ticket')} />
                <LoaderMainLogo />
            </div>
        );
    }

    const userOptions = users.map(user => ({
        value: String(user.id),
        label: user.email || user.name || `User ${user.id}`,
    }));

    const statusOptions = TICKET_STATUSES.map(status => ({
        value: status,
        label: t(`status_${status}`),
    }));

    const priorityOptions = TICKET_PRIORITIES.map(priority => ({
        value: priority,
        label: t(`priority_${priority}`),
    }));

    const currentLang = i18next.language || 'es';
    const getDueRelativeText = (dueDatetime: Date | string | null) => {
        if (!dueDatetime) return null;
        return dayjs(dueDatetime).locale(currentLang).fromNow();
    };

    return (
        <div>
            <div className="panel-content">
                <div className="max-w-5xl mx-auto">
                    <div className="mb-4 flex items-center justify-between">
                        <Button
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => navigate(TICKETS_PATH)}
                            leftSection={<IconArrowLeft size={16} />}
                        >
                            {t('back_to_tickets')}
                        </Button>
                    </div>

                    {/* Controls Bar - Auto Saves on Selection Change */}
                    <div className="mb-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    {t('status')}
                                </label>
                                <Select
                                    data={statusOptions}
                                    value={formData.status}
                                    onChange={(value) => handleMetaChange('status', value as Ticket['status'])}
                                    size="sm"
                                    radius="md"
                                    disabled={updateMutation.isPending}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    {t('priority')}
                                </label>
                                <Select
                                    data={priorityOptions}
                                    value={formData.priority}
                                    onChange={(value) => handleMetaChange('priority', value as Ticket['priority'])}
                                    size="sm"
                                    radius="md"
                                    disabled={updateMutation.isPending}
                                    leftSection={
                                        <span className={`w-2.5 h-2.5 rounded-full ${PRIORITY_COLORS[formData.priority || 'medium']?.dot || 'bg-gray-400'}`} />
                                    }
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    {t('assignee')}
                                </label>
                                <Select
                                    placeholder={t('unassigned')}
                                    data={userOptions}
                                    value={formData.assignee_id ? String(formData.assignee_id) : null}
                                    onChange={(value) => handleMetaChange('assignee_id', value ? Number(value) : null)}
                                    clearable
                                    searchable
                                    size="sm"
                                    radius="md"
                                    disabled={updateMutation.isPending}
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('date')}
                                    </label>
                                    {formData.due_datetime && (
                                        <span className={`text-[11px] font-medium ${dayjs(formData.due_datetime).isBefore(dayjs()) ? 'text-red-600 font-semibold' : 'text-blue-600'}`}>
                                            {getDueRelativeText(formData.due_datetime)}
                                        </span>
                                    )}
                                </div>
                                <DatePickerCustom
                                    hideLabel
                                    placeholder={t('no_due_date')}
                                    value={formData.due_datetime ? dayjs(formData.due_datetime).format('YYYY-MM-DD') : ''}
                                    onChange={([date]) => handleMetaChange('due_datetime', date ? dayjs(date).toDate() : null)}
                                    clearable
                                    size="sm"
                                    disabled={updateMutation.isPending}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">

                        {/* Title & Content Editor */}
                        <div className="min-h-[200px]">
                            <TicketEditor
                                title={formData.title || ''}
                                content={formData.content || ''}
                                onTitleChange={(title) => setFormData(prev => ({ ...prev, title }))}
                                onContentChange={(content) => setFormData(prev => ({ ...prev, content }))}
                                titlePlaceholder={t('ticket_title_placeholder')}
                                contentPlaceholder={t('ticket_content_placeholder')}
                            />
                        </div>

                        {/* Save Button right below Editor */}
                        <div className="flex items-center justify-end gap-3 mt-4 mb-6">
                            <Button
                                onClick={handleSaveTitleContent}
                                color="black"
                                size="sm"
                                radius="md"
                                loading={createMutation.isPending || updateMutation.isPending}
                                disabled={!formData.title?.trim()}
                            >
                                {isNew ? t('create_ticket') : t('save')}
                            </Button>
                        </div>

                        {/* Comments */}
                        {!isNew && ticket && (
                            <div className="mt-8 pt-8 border-t border-gray-100">
                                <TicketComments ticket={ticket} />
                            </div>
                        )}

                    </div>
                    {!isNew && ticket && (
                        <div className="flex items-center gap-6 text-xs text-gray-500 m-4">
                            <span className="font-medium text-gray-700">
                                {ticket.creator?.name || ticket.creator?.email || t('unknown')}
                            </span>
                            <span>{t('opened_on')} {new Date(ticket.created_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{ticket.comments?.length || 0} {t('comments')}</span>
                            {!isNew && (
                                <Button
                                    onClick={handleDelete}
                                    color="red"
                                    variant="subtle"
                                    size="xs"
                                    radius="md"
                                    loading={deleteMutation.isPending}
                                    leftSection={<IconTrash size={16} />}
                                >
                                    {t('delete_ticket')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
